import { roundBalance } from '../../common/tools';
import { doUntilDone } from '../tools';
import { contractStatus, contractSettled, contract as broadcastContract } from '../broadcast';
import { sell, openContractReceived } from './state/actions';
import { observer } from '../../../common/utils/observer';
import config from '../../common/const';

const AFTER_FINISH_TIMEOUT = 5;

export default Engine =>
    class OpenContract extends Engine {
        observeOpenContract() {
            const handleContract = (contract, dataContainer) => {
                if (!this.expectedContractId(contract.contract_id)) {
                    return;
                }

                this.setContractFlags(contract);

                // Create a new object to avoid direct assignment to the function parameter
                const updatedContract = { ...contract, debugId: dataContainer.contract.debugId };

                if (this.isExpired && updatedContract.transaction_ids) {
                    // isSold
                    const sellId = String(updatedContract.transaction_ids.sell);
                    updatedContract.transaction_ids.sell = config.debug
                        ? Number(`${config.transactionId}${sellId.slice(-8, -2)}1`)
                        : sellId;
                }

                // Create a new object for dataContainer to avoid direct assignment
                const updatedDataContainer = { ...dataContainer, contract: updatedContract };

                broadcastContract({ accountID: this.accountInfo?.loginid, ...updatedContract });

                if (
                    (this.isExpired && !['ACCU', 'MULTUP', 'MULTDOWN'].includes(updatedContract.contract_type)) ||
                    (this.isSold && !this.isExpired) ||
                    this.isSold
                ) {
                    // isSold
                    this.handleSoldContract(updatedContract);
                } else {
                    this.handleOpenContract();
                }

                // Update the original dataContainer with the new contract
                Object.assign(dataContainer, updatedDataContainer);
            };

            this.listen('proposal_open_contract', r => handleContract(r.proposal_open_contract, this.data));
            this.listenVirtual('proposal_open_contract', r =>
                handleContract(r.proposal_open_contract, this.dataVirtual)
            );
        }

        handleSoldContract(contract) {
            contractStatus({
                id  : 'contract.sold',
                data: contract.transaction_ids?.sell,
                contract,
            });

            contractSettled(contract);
            this.contractId = '';
            this.updateTotals(contract);

            if (this.afterPromise) {
                this.afterPromise();
            }

            this.store.dispatch(sell());
            this.cancelSubscriptionTimeout();
            this.onTrade = false;
        }

        handleOpenContract() {
            this.store.dispatch(openContractReceived());

            if (!this.isExpired) {
                this.resetSubscriptionTimeout();
            }
        }

        waitForAfter() {
            return new Promise(resolve => {
                this.afterPromise = resolve;
            });
        }

        subscribeToOpenContract(contractId = this.contractId) {
            if (this.contractId !== contractId) {
                this.resetSubscriptionTimeout();
            }
            this.contractId = contractId;

            doUntilDone(() => {
                const api = this.getApi();
                return api.subscribeToOpenContract(contractId).then(response => {
                    this.openContractId = response.proposal_open_contract.id;
                    this.actualContract = response.proposal_open_contract;
                });
            }).catch(error => {
                observer.emit('reset_animation');
                observer.emit('Error', error);
            });
        }

        resetSubscriptionTimeout(timeout = this.getContractDuration() + AFTER_FINISH_TIMEOUT) {
            this.cancelSubscriptionTimeout();
            this.subscriptionTimeout = setInterval(() => {
                this.subscribeToOpenContract();
                this.resetSubscriptionTimeout(timeout);
                this.getApi().getPortfolio();
            }, timeout * 1000);
        }

        cancelSubscriptionTimeout() {
            clearTimeout(this.subscriptionTimeout);
        }

        setContractFlags(contract) {
            const {
                is_expired: isExpired,
                is_valid_to_sell: isValidToSell,
                is_sold: isSold,
                entry_tick: entryTick,
            } = contract;

            this.isSold = Boolean(isSold);
            this.isSellAvailable = !this.isSold && Boolean(isValidToSell);
            this.isExpired = Boolean(isExpired);
            this.hasEntryTick = Boolean(entryTick);
        }
        expectedContractId(contractId) {
            return this.contractId && contractId === this.contractId;
        }

        getSellPrice() {
            const { bid_price: bidPrice, buy_price: buyPrice, currency } = this.getCurrentContract();
            return Number(roundBalance({ currency, balance: Number(bidPrice) - Number(buyPrice) }));
        }

        getApi() {
            return this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing
                ? this.virtualApi
                : this.api;
        }

        getCurrentContract() {
            return this.virtualSettings.active && this.virtualSettings.valid && this.virtualSettings.ongoing
                ? this.dataVirtual.contract
                : this.data.contract;
        }
    };
